import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import AdList from '../components/AdList';
import AdListShimmer from '../components/AdListShimmer';
import FilterSidebar from '../components/FilterSidebar';
import axios from '../api';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const Home = ({ selectedCity }) => {
  const [ads, setAds] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const [filters, setFilters] = useState({ category: '', price_min: '', price_max: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Refs to avoid stale closures in scroll handler
  const currentParamsRef = useRef({});
  const currentPageRef = useRef(1);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    document.title = 'آگهی‌ها | آگاه‌جو';
  }, []);

  const fetchPage = useCallback(async (params, page, append = false) => {
    if (isFetchingRef.current && append) return;
    isFetchingRef.current = true;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await axios.get('/ads/', { params: { ...params, page } });
      const data = res.data;
      const results = Array.isArray(data) ? data : (data.results ?? []);
      const next = Array.isArray(data) ? null : data.next;
      if (append) {
        setAds(prev => [...prev, ...results]);
      } else {
        setAds(results);
        setHasFetched(true);
      }
      setHasMore(!!next);
    } catch {
      if (!append) { setAds([]); setHasFetched(true); }
    }
    if (append) setLoadingMore(false); else setLoading(false);
    isFetchingRef.current = false;
  }, []);

  // Load page 1 when city or URL filters change
  useEffect(() => {
    const urlFilters = {};
    for (const [key, value] of searchParams.entries()) {
      urlFilters[key] = value;
    }
    if (selectedCity) urlFilters.city = selectedCity;
    currentParamsRef.current = urlFilters;
    currentPageRef.current = 1;
    setFilters(prev => ({ ...prev, ...urlFilters }));
    fetchPage(urlFilters, 1, false);
    // eslint-disable-next-line
  }, [selectedCity, searchParams]);

  // Infinite scroll: detect when user is near the bottom
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 400 &&
        hasMore &&
        !isFetchingRef.current
      ) {
        const nextPage = currentPageRef.current + 1;
        currentPageRef.current = nextPage;
        fetchPage(currentParamsRef.current, nextPage, true);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, fetchPage]);

  const handleSidebarSearch = async (params) => {
    setLoading(true);
    setFilters(prev => ({ ...prev, ...params }));
    const urlParams = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== '' && params[key] !== undefined && params[key] !== null && params[key] !== false) {
        urlParams[key] = params[key];
      }
    });
    setSearchParams(urlParams);
    if (selectedCity) urlParams.city = selectedCity;
    currentParamsRef.current = urlParams;
    currentPageRef.current = 1;
    await fetchPage(urlParams, 1, false);
    setHasFetched(true);
  };

  const handleFilterChange = e => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', direction: 'rtl', display: 'flex', flexDirection: isMobile ? 'column' : 'row', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw' }}>
      {isMobile ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
            <IconButton onClick={() => setDrawerOpen(true)} size="large" sx={{ bgcolor: 'white', border: '1px solid #e0e3e7', borderRadius: 2 }}>
              <MenuIcon />
            </IconButton>
          </Box>
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: 260, borderRadius: '24px 0 0 24px', p: 2 } }}
          >
            <Box sx={{ p: 2 }}>
              <FilterSidebar
                filters={filters}
                onChange={handleFilterChange}
                onSearch={async params => {
                  await handleSidebarSearch(params);
                  setDrawerOpen(false);
                }}
                loading={loading}
              />
            </Box>
          </Drawer>
          <Box sx={{ flex: 1, width: '100%', maxWidth: 600, mx: 'auto', px: 1 }}>
            {loading ? <AdListShimmer /> : <AdList ads={ads} loading={loading} hasFetched={hasFetched} loadingMore={loadingMore} vertical />}
          </Box>
        </>
      ) : (
        <>
          <Box style={{ width: '240px', minWidth: '180px', maxWidth: '270px', background: '#fff', borderLeft: '1px solid #e0e3e7', boxShadow: '0 8px 32px rgba(60,60,60,0.16)', borderRadius: 24, padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'stretch', position: 'relative', zIndex: 10, marginTop: 0, marginRight: 24 }}>
            <FilterSidebar filters={filters} onChange={handleFilterChange} onSearch={handleSidebarSearch} loading={loading} />
          </Box>
          <Box style={{ flex: 1, maxWidth: 1200, margin: '0 auto' }}>
            {loading ? <AdListShimmer /> : <AdList ads={ads} loading={loading} hasFetched={hasFetched} loadingMore={loadingMore} />}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Home;
